import { useMutation, useQuery } from "@tanstack/react-query";

import { QUERY_KEYS } from "@/api/constants";
import { RESOURCE_QUERY_META } from "@/api/resourceQuery";
import type { PaginationParams } from "@/types/api";

import type {
  AdminDinerResponse,
  AssignmentsUserRole,
  IUserBasicInfo,
  IUserDetails,
  IUserInvite,
} from "./types";
import { usersApi } from "./usersApi";

export const useInviteUserMutation = () => {
  return useMutation({
    mutationFn: (data: IUserInvite) => {
      return usersApi.postNewUserInvite(data);
    },
  });
};

export const useGetAllUsersQuery = (
  params?: PaginationParams,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.adminUsers, params],
    queryFn: () => usersApi.getAllUsers(params),
    enabled: enabled,
  });
};

export const useGetUserByIdQuery = (id: string) => {
  return useQuery<IUserDetails>({
    queryKey: [QUERY_KEYS.adminUsers, id],
    queryFn: () => usersApi.getUserById(id),
    enabled: !!id,
    meta: RESOURCE_QUERY_META,
  });
};

export const useUpdateUserMutation = () => {
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<IUserBasicInfo>;
    }) => {
      return usersApi.patchUser(id, data);
    },
  });
};

export const useUnassignmentUserMutation = () => {
  return useMutation({
    mutationFn: (id: string) => {
      return usersApi.unassignmentUserRole(id);
    },
  });
};

export const useUpdateAssignmentRoleMutation = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AssignmentsUserRole }) => {
      return usersApi.patchAssignmentUserRole(id, data);
    },
  });
};

export const useNewAssignRoleMutation = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AssignmentsUserRole }) => {
      return usersApi.postNewAssignmentRole(id, data);
    },
  });
};

export const useGetUserAssignedRoleByIdQuery = (
  id: string,
  params?: PaginationParams
) => {
  return useQuery<AdminDinerResponse>({
    queryKey: [QUERY_KEYS.adminUserRole, id, params],
    queryFn: () => usersApi.getUserAssignedRoleById(id, params),
    enabled: !!id,
  });
};

export const useResendInvitationMutation = () => {
  return useMutation({
    mutationFn: (id: string) => {
      return usersApi.postResendInvitation(id);
    },
  });
};

export const useReactivateUserMutation = () => {
  return useMutation({
    mutationFn: (id: string) => {
      return usersApi.postReactivateUser(id);
    },
  });
};

export const useGetPartnerScopeUsersQuery = (
  params: PaginationParams,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.partnerScopeUsers, params],
    queryFn: () => usersApi.getPartnerScopeUsers(params),
    enabled,
  });
};
